/*! Copyright (c) Safe As Milk. All rights reserved. */
import { formatMoney } from "utils";

class PayIn4 extends HTMLElement {
    #boundOnVariantChange;
    #variantPrices;
    #installments;
    #moneyFormat;

    constructor() {
        super();
        this.#boundOnVariantChange = this.#onVariantChange.bind(this);
    }

    connectedCallback() {
        this.#installments = Math.max(1, Number(this.dataset.installments) || 4);
        this.#moneyFormat = this.dataset.moneyFormat || (window.theme && window.theme.money_format) || "${{amount}}";
        try {
            this.#variantPrices = JSON.parse(this.dataset.variantPrices || "{}");
        } catch (e) {
            this.#variantPrices = {};
        }
        this.amountElement = this.querySelector("[data-pay-in-4-amount]");

        // Tooltip click-toggle
        this.#trigger = this.querySelector(".pay-in-4__info-trigger");
        this.#tooltip = this.querySelector(".pay-in-4__tooltip");
        if (this.#trigger && this.#tooltip) {
            this.#boundOnTriggerClick = this.#onTriggerClick.bind(this);
            this.#boundOnDocClick = this.#onDocClick.bind(this);
            this.#boundOnKeydown = this.#onKeydown.bind(this);
            this.#trigger.addEventListener("click", this.#boundOnTriggerClick);
            document.addEventListener("click", this.#boundOnDocClick);
            document.addEventListener("keydown", this.#boundOnKeydown);
        }

        // Listen to variant changes from any selector inside the same product-single.
        const root = this.closest("product-single") || document;
        const selectors = root.querySelectorAll(
            "variant-selects, variant-radios, variant-swatches, variant-mixed-inputs"
        );
        selectors.forEach((selector) => {
            selector.addEventListener("on:variant:change", this.#boundOnVariantChange);
        });
        this.#variantSelectors = selectors;
    }

    disconnectedCallback() {
        if (this.#variantSelectors) {
            this.#variantSelectors.forEach((selector) => {
                selector.removeEventListener("on:variant:change", this.#boundOnVariantChange);
            });
        }
        if (this.#trigger && this.#boundOnTriggerClick) {
            this.#trigger.removeEventListener("click", this.#boundOnTriggerClick);
        }
        if (this.#boundOnDocClick) {
            document.removeEventListener("click", this.#boundOnDocClick);
        }
        if (this.#boundOnKeydown) {
            document.removeEventListener("keydown", this.#boundOnKeydown);
        }
    }

    #variantSelectors;
    #trigger;
    #tooltip;
    #boundOnTriggerClick;
    #boundOnDocClick;
    #boundOnKeydown;

    #setTooltipOpen(open) {
        if (!this.#tooltip || !this.#trigger) return;
        if (open) {
            this.#tooltip.removeAttribute("hidden");
            this.#trigger.setAttribute("aria-expanded", "true");
        } else {
            this.#tooltip.setAttribute("hidden", "");
            this.#trigger.setAttribute("aria-expanded", "false");
        }
    }

    #onTriggerClick(event) {
        event.preventDefault();
        event.stopPropagation();
        const isOpen = !this.#tooltip.hasAttribute("hidden");
        this.#setTooltipOpen(!isOpen);
    }

    #onDocClick(event) {
        if (!this.#tooltip || this.#tooltip.hasAttribute("hidden")) return;
        if (this.contains(event.target)) return;
        this.#setTooltipOpen(false);
    }

    #onKeydown(event) {
        if (event.key === "Escape" && this.#tooltip && !this.#tooltip.hasAttribute("hidden")) {
            this.#setTooltipOpen(false);
            this.#trigger?.focus();
        }
    }

    #onVariantChange(event) {
        const variant = event?.detail?.variant;
        if (!variant || !this.amountElement) return;
        const price = this.#variantPrices[variant.id] ?? variant.price;
        if (typeof price !== "number") return;
        const perInstallment = Math.floor(price / this.#installments);
        this.amountElement.innerHTML = formatMoney(perInstallment, this.#moneyFormat);
    }
}

if (!customElements.get("pay-in-4")) {
    customElements.define("pay-in-4", PayIn4);
}
